/* eslint-disable max-lines */
import React, {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Challenge, ChallengeMode, Competence, Scalars, UserInputAction, UserInputActionInput} from "../../../schema";
import {gql} from "@apollo/client";
import {useFormState} from "../../../hooks/useFormState";
import {INPUT_PHRASE_MAX_LENGTH} from "../../../settings";

import classes from "./forms.module.css";

import Button from "../../../ui/button";
import WithTooltip from "../../../ui/tooltip";
import {HelpCircleIcon} from "../../../ui/icons";
import Checkbox from "../../../ui/checkbox";
import LimitedTextarea from "../LimitedTextarea";
import PhrasesEdit from "../PhrasesEdit";
import Link from "../../../ui/link";
import {supportedPhraseTags} from "../../Phrase";
import CompetenciesCreatableSelect from "../CompetenciesCreatableSelect";

type props = {
  action: UserInputAction;
  competencies?: Competence[];
  phraseLimit?: Scalars["Int"];
  onSubmit?: (data: UserInputActionInput) => void;
  testId?: string;
  challenge?: Challenge;
};

type formState = Omit<UserInputActionInput, "beforeActionId" | "freeSpeech"> & {
  questionText: string;
  referenceText: string;
  hintText: NonNullable<UserInputActionInput["hintText"]>;
  analogTexts: string[];
  freeSpeech?: "on";
};

export default function UserInputActionEditForm({action, competencies, challenge,
   phraseLimit, onSubmit, testId}: props) {
  const {t} = useTranslation();

  const [hintTextVisibility, setHintTextVisibility] = useState(!!action.hintText);
  const showHintTextArea = useCallback(() => setHintTextVisibility(true), []);

  const [analogTextsVisibility, setAnalogTextsVisibility] = useState(action.analogTexts.length !== 0);
  const showAnalogTextsArea = useCallback(() => setAnalogTextsVisibility(true), []);

  const formState = useFormState<formState>({
    initialValues: {
      questionText: action.questionText ?? "",
      referenceText: action.referenceText ?? "",
      hintText: action.hintText ?? "",
      analogTexts: action.analogTexts,
      freeSpeech: action.freeSpeech ? "on" : undefined,
      competenceId: action.competence?.id ?? undefined,
    },
    preventDefault: true,
    onSubmit: (data) => {
      onSubmit &&
        onSubmit({
          ...data,
          hintText: data.hintText ? data.hintText : "",
          freeSpeech: data.freeSpeech === "on",
        });
    },
  });

  const isFreeSpeech = formState.values.freeSpeech === "on";

  const updatePhrases = useCallback(
    (analogTexts: string[]) => {
      formState.setValues({analogTexts});
    },
    [formState],
  );

  const analogTextsTextareaProps = useMemo(
    () => ({
      maxLength: phraseLimit ?? INPUT_PHRASE_MAX_LENGTH,
      rows: 4,
      autoComplete: "off",
      placeholder: t("components.ActionEditForm.analogTexts.placeholder"),
    }),
    [t, phraseLimit],
  );

  const updateCompetence = useCallback(
    (id?: string) => {
      formState.setValues({competenceId: id ?? null});
    },
    [formState],
  );

  return (
    <form data-testid={`UserInputActionEditForm:${testId}`} method="post" onSubmit={formState.submitHandler}>
      {challenge && challenge.mode === ChallengeMode.COMPETENCE_TEST && (
        <div className={classes.row}>
          <label className={classes.label}>
            {t("components.ActionEditForm.competence.label")}{" "}
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ActionEditForm.competence.helpText")}
            >
              <HelpCircleIcon />
            </WithTooltip>
          </label>
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.ActionEditForm.competence.helpText")}
          >
            <CompetenciesCreatableSelect
              competencies={competencies ?? []}
              currentItemIds={formState.values.competenceId ?? undefined}
              onSelect={updateCompetence}
              isMulti={false}
            />
          </WithTooltip>
        </div>
      )}

      {challenge && challenge.mode === ChallengeMode.COMPETENCE_TEST && (
        <div className={classes.row}>
          <label className={classes.label} htmlFor="questionText">
            {t("components.ActionEditForm.questionText.title")}
          </label>
          <LimitedTextarea
            maxLength={phraseLimit ?? INPUT_PHRASE_MAX_LENGTH}
            filters={supportedPhraseTags}
            id="questionText"
            name="questionText"
            rows={4}
            value={formState.values.questionText}
            onChange={formState.changeHandler}
            placeholder={t("components.ActionEditForm.questionText.placeholder")}
          />
        </div>
      )}

      {challenge && challenge.mode !== ChallengeMode.COMPETENCE_TEST && (
        <div className={classes.row}>
          <label className={classes.label} htmlFor="referenceText">
            {isFreeSpeech
            ? t("components.ActionEditForm.freeSpeechText.title")
            : t("components.ActionEditForm.referenceText.titleForModes")}
            {" "}
            <WithTooltip
              className={classes.help} as='span'
              helpText={isFreeSpeech
              ? t("components.ActionEditForm.freeSpeechText.help")
              : t("components.ActionEditForm.referenceText.helpForModes")}
            >
              <HelpCircleIcon/>
            </WithTooltip>
          </label>
          <LimitedTextarea
            maxLength={phraseLimit ?? INPUT_PHRASE_MAX_LENGTH}
            filters={supportedPhraseTags}
            id="referenceText"
            name="referenceText"
            rows={4}
            value={formState.values.referenceText}
            onChange={formState.changeHandler}
            placeholder={isFreeSpeech
            ? t("components.ActionEditForm.freeSpeechText.placeholder")
            : t("components.ActionEditForm.referenceText.placeholderForModes")}
          />
        </div>
      )}

      {!hintTextVisibility && (
        <div className={classes.row}>
          <Link onClick={showHintTextArea}>{t("components.ActionEditForm.hintText.showArea")}</Link>
        </div>
      )}

      {hintTextVisibility && (
        <div className={classes.row}>
          <label className={classes.label} htmlFor="hintText">
            {t("components.ActionEditForm.hintText.title")}{" "}
            <WithTooltip className={classes.help} as="span" helpText={t("components.ActionEditForm.hintText.help")}>
              <HelpCircleIcon />
            </WithTooltip>
          </label>
          <LimitedTextarea
            maxLength={phraseLimit ?? INPUT_PHRASE_MAX_LENGTH}
            id="hintText"
            name="hintText"
            rows={4}
            value={formState.values.hintText}
            onChange={formState.changeHandler}
            placeholder={t("components.ActionEditForm.hintText.placeholder")}
          />
        </div>
      )}

      {challenge && challenge.mode === ChallengeMode.COMPETENCE_TEST && (
        <div className={classes.row}>
          <label className={classes.label} htmlFor="referenceText">
            {t("components.ActionEditForm.referenceText.title")}
          </label>
          <LimitedTextarea
            maxLength={phraseLimit ?? INPUT_PHRASE_MAX_LENGTH}
            filters={supportedPhraseTags}
            id="referenceText"
            name="referenceText"
            rows={4}
            value={formState.values.referenceText}
            onChange={formState.changeHandler}
            placeholder={t("components.ActionEditForm.referenceText.placeholder")}
          />
        </div>
      )}

      {!isFreeSpeech && !analogTextsVisibility && (
        <div className={classes.row}>
          <Link onClick={showAnalogTextsArea}>{t("components.ActionEditForm.analogTexts.showArea")}</Link>
        </div>
      )}

      {!isFreeSpeech && analogTextsVisibility && (
        <>
          <div className={classes.row}>
            <label className={classes.label} htmlFor="analogTexts">
              {t("components.ActionEditForm.analogTexts.title")}{" "}
              <WithTooltip
                className={classes.help}
                as="span"
                helpText={t("components.ActionEditForm.analogTexts.help")}
              >
                <HelpCircleIcon />
              </WithTooltip>
            </label>
            <div className={formState.values.analogTexts.length > 7 ? classes.yScroll : ""}>
              <PhrasesEdit
                value={formState.values.analogTexts}
                onValueChange={updatePhrases}
                textareaProps={analogTextsTextareaProps}
              />
            </div>
          </div>
        </>
      )}

      <div className={classes.row}>
        <label>
          <Checkbox checked={isFreeSpeech} name="freeSpeech" onChange={formState.changeHandler} />
          <span className={classes.checkboxText}>
            {t("components.ActionEditForm.freeSpeech.title")}{" "}
            <WithTooltip className={classes.help} as="span" helpText={t("components.ActionEditForm.freeSpeech.help")}>
              <HelpCircleIcon />
            </WithTooltip>
          </span>
        </label>
      </div>

      <div className={classes.row}>
        <Button color={"success"}>{t("common.save")}</Button>
      </div>
    </form>
  );
}

UserInputActionEditForm.fragments = {
  root: gql`
    fragment UserInputActionEditForm on UserInputAction {
      id
      hintText
      analogTexts
      freeSpeech
      referenceText
      questionText
      competence {
        id
        name
        __typename
      }
    }
  `,
};
